import React, {useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import {edit, listSystemType, listType} from '../../../lib/api/competition';
import {listGender, listSelection} from '../../../lib/api/player';
import {currentSeason} from '../../../lib/api/season';

const parseAllowedDays = allowedDays => {
  if (Array.isArray(allowedDays)) {
    return allowedDays.map(Number);
  }

  if (typeof allowedDays === 'string' && allowedDays.trim()) {
    try {
      const parsed = JSON.parse(allowedDays);
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

const daysOfWeek = [
  { value: 1, label: 'Ponedjeljak' },
  { value: 2, label: 'Utorak' },
  { value: 3, label: 'Srijeda' },
  { value: 4, label: 'Četvrtak' },
  { value: 5, label: 'Petak' },
  { value: 6, label: 'Subota' },
  { value: 0, label: 'Nedjelja' }
];

export function Edit({ initialData, onClose, onSave }) {
  const [competition, setCompetition] = useState({
    ...initialData,
    startDate: initialData?.startDate || initialData?.start_date?.split('T')[0] || '',
    allowedDays: parseAllowedDays(initialData?.allowedDays ?? initialData?.allowed_days),
    competition_system_type_id: initialData?.competition_system_type_id || 1,
    sets_to_win: initialData?.sets_to_win || 3
  });
  const [competitionsType, setCompetitionsType] = useState([]);
  const [competitionSystemTypes, setCompetitionSystemTypes] = useState([]);
  const [genderType, setGenderType] = useState([]);
  const [season, setSeason] = useState([]);
  const [selection, setSelection] = useState([]);

  const [errors, setErrors] = useState({
    competitions_type_id: false,
    name: false,
    season_id: false,
    gender_type_id: false,
    selection_id: false,
    startDate: false,
    allowedDays: false,
    competition_system_type_id: false,
    sets_to_win: false,
  });

  useEffect(() => {
    (async () => {
      const resType = await listType();
      const resSystemType = await listSystemType();
      setCompetitionsType(resType);
      setCompetitionSystemTypes(resSystemType);
      const resGender = await listGender();
      setGenderType(resGender);
      const resSeason = await currentSeason();
      setSeason(resSeason);
      const rowSelection = await listSelection();
      setSelection(rowSelection);
    })();
  }, []);

  const handelInput = e => {
    const { name, value } = e.target;
    setCompetition({ ...competition, [name]: value });
  };

  const handleSwitch = e => {
    const { name, checked } = e.target;
    const value = checked ? 1 : 0;
    setCompetition({ ...competition, [name]: value });
  };

  const handleAllowedDaysChange = e => {
    const dayValue = Number(e.target.value);
    const nextAllowedDays = e.target.checked
      ? [...competition.allowedDays, dayValue]
      : competition.allowedDays.filter(day => day !== dayValue);

    setCompetition({
      ...competition,
      allowedDays: nextAllowedDays
    });
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validateForm(event)) return;

    const obj = {
      competitions_type_id: competition.competitions_type_id,
      name: competition.name,
      season_id: competition.season_id,
      gender_type_id: competition.gender_type_id,
      selection_id: competition.selection_id,
      is_locked: competition.is_locked ? 1 : 0,
      startDate: competition.startDate,
      allowedDays: competition.allowedDays.map(Number),
      competition_system_type_id: competition.competition_system_type_id || 1,
      sets_to_win: Number(competition.sets_to_win)
    };

    await edit(obj, competition.competitions_guid);

    onSave();
    onClose();
  };

  const validateForm = event => {
    const form = event.currentTarget;
    const newErrors = {
      competitions_type_id: !form.competitions_type_id.value,
      name: !form.name.value.trim(),
      season_id: !form.season_id.value,
      gender_type_id: !form.gender_type_id.value,
      selection_id: !form.selection_id.value,
      startDate: !form.startDate.value,
      allowedDays: !competition.allowedDays.length,
      competition_system_type_id: !form.competition_system_type_id.value,
      sets_to_win: !['2', '3'].includes(form.sets_to_win.value),
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some(error => error)) {
      event.stopPropagation();
      return false;
    }
    return true;
  };

  return (
      <Card>
        <Card.Body className="bg-body-tertiary">
          <Form
              method="PUT"
              encType="multipart/form-data"
              noValidate
              onSubmit={handleSubmit}
          >

            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="competitions_type_id">
                <Form.Label>Vrsta takmičenja:</Form.Label>
                <Form.Select name="competitions_type_id" value={competition.competitions_type_id || ''} onChange={handelInput} isInvalid={errors.competitions_type_id}>
                  <option value="">Izaberite</option>
                  {competitionsType.map(p => <option key={p.competitions_type_id} value={p.competitions_type_id}>{p.name}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Molimo izaberite vrstu takmičenja</Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="12" controlId="name">
                <Form.Label>Naziv:</Form.Label>
                <Form.Control required type="text" name="name" value={competition.name || ''} onChange={handelInput} isInvalid={errors.name} />
                <Form.Control.Feedback type="invalid">Molimo unesite naziv</Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="season_id">
                <Form.Label>Sezona:</Form.Label>
                <Form.Select name="season_id" value={competition.season_id || ''} onChange={handelInput} isInvalid={errors.season_id}>
                  {season.map(p => <option key={p.season_id} value={p.season_id}>{p.name}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Molimo izaberite sezonu</Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md="6" controlId="gender_type_id">
                <Form.Label>Pol:</Form.Label>
                <Form.Select name="gender_type_id" value={competition.gender_type_id || ''} onChange={handelInput} isInvalid={errors.gender_type_id}>
                  <option value="">Izaberite</option>
                  {genderType.map(p => <option key={p.gender_type_id} value={p.gender_type_id}>{p.name}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Molimo izaberite pol</Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-5">
              <Form.Group as={Col} md="6" controlId="selection_id">
                <Form.Label>Selekcija:</Form.Label>
                <Form.Select name="selection_id" value={competition.selection_id || ''} onChange={handelInput} isInvalid={errors.selection_id}>
                  <option value="">Izaberite</option>
                  {selection.map(p => <option key={p.selection_id} value={p.selection_id}>{p.name}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Molimo izaberite selekciju</Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md="6" controlId="is_locked">
                <Form.Label>&nbsp;</Form.Label>
                <Form.Check
                    type='switch'
                    id='is_locked'
                    name='is_locked'
                    onChange={handleSwitch}
                    checked={!!competition.is_locked}
                    label='Zaključaj izmjene klubovima'
                />
              </Form.Group>
            </Row>

            <hr className="my-4" />

            <Row className="mb-3">
              <Form.Group as={Col} md="4" controlId="startDate">
                <Form.Label>Početak takmičenja:</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={competition.startDate || ''}
                  onChange={handelInput}
                  isInvalid={errors.startDate}
                />
                <Form.Control.Feedback type="invalid">Molimo unesite početak takmičenja</Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md="4" controlId="competition_system_type_id">
                <Form.Label>Sistem takmičenja:</Form.Label>
                <Form.Select
                  name="competition_system_type_id"
                  value={competition.competition_system_type_id || 1}
                  onChange={handelInput}
                  isInvalid={errors.competition_system_type_id}
                >
                  {competitionSystemTypes.map(p => (
                    <option key={p.competition_system_type_id} value={p.competition_system_type_id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Molimo izaberite sistem takmičenja</Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col} md="4" controlId="sets_to_win">
                <Form.Label>Broj dobijenih setova:</Form.Label>
                <Form.Select
                  name="sets_to_win"
                  value={competition.sets_to_win || 3}
                  onChange={handelInput}
                  isInvalid={errors.sets_to_win}
                >
                  <option value="2">2</option>
                  <option value="3">3</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">Molimo izaberite broj dobijenih setova</Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-4">
              <Form.Group as={Col} md="12" controlId="allowedDays">
                <Form.Label>Dani odigravanja:</Form.Label>
                <div className={errors.allowedDays ? 'border border-danger rounded p-3' : 'border rounded p-3'}>
                  <Row>
                    {daysOfWeek.map(day => (
                      <Col md="3" key={day.value} className="mb-2">
                        <Form.Check
                          type="checkbox"
                          id={`allowedDays-${day.value}`}
                          name="allowedDays"
                          value={day.value}
                          checked={competition.allowedDays.includes(day.value)}
                          onChange={handleAllowedDaysChange}
                          label={day.label}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
                {errors.allowedDays && (
                  <div className="text-danger mt-1">Molimo izaberite barem jedan dan</div>
                )}
              </Form.Group>
            </Row>

            <Button type="submit">Sačuvaj</Button>
          </Form>
        </Card.Body>
      </Card>
  );
}
